import { Modal, Button, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import {
  JournalVersionsListPanel,
  type JournalVersion,
} from "@/features/journal-version-list";
import { useCoordinator } from "@/shared/lib/hooks";
import { useAppRoutes } from "@shared/lib/react-router";
import {
  useNotifyVersionsForbidden,
  VERSIONS_ACCESS_DENIED_MESSAGE,
} from "../../../../../model/hooks/use-notify-versions-forbidden";
import { isForbiddenError } from "../../../../../model/utils/get-http-error-status";
import { useGetItemsVersion } from "../../../model/use-get-items-version";
import "./journal-version-modal.scss";

export type JournalVersionModalProps = {
  open: boolean;
  onCancel: () => void;
  sectionThreeListId?: string;
  itemNoVersionId?: string;
  sequenceNumber?: number | null;
};

const mapApiVersions = (section3List: any[]): JournalVersion[] => {
  const sorted = [...section3List].sort(
    (a, b) =>
      (b.versionInfo?.versionNumber ?? 0) - (a.versionInfo?.versionNumber ?? 0),
  );

  return sorted.map((entry, index) => {
    const signer = entry.content?.signer;
    const signerName = signer
      ? `${signer.surname ?? ""} ${signer.firstName?.[0] ?? ""}.${signer.middleName?.[0] ?? ""}.`.trim()
      : null;

    return {
      versionId:
        entry.versionInfo?.versionId ??
        entry.versionInfo?.itemNoVersionId ??
        entry.itemNoVersionId ??
        String(index),
      versionNumber: entry.versionInfo?.versionNumber ?? index + 1,
      status: entry.versionInfo?.isCurrent ? "ACTUAL" : "OUTDATED",
      integrationStatus: null,
      recordStatus: entry.versionInfo?.status ?? null,
      versionDate:
        entry.versionInfo?.updateDate ?? entry.versionInfo?.createDate ?? "",
      responsible: signerName
        ? { name: signerName, role: signer.position ?? "" }
        : null,
      changeReason: entry.versionInfo?.actionDetails ?? null,
    };
  });
};

export const JournalVersionModal = ({
  open,
  onCancel,
  sectionThreeListId,
  itemNoVersionId,
  sequenceNumber,
}: JournalVersionModalProps) => {
  const navigate = useNavigate();
  const { projectId, objectId, mainJournalId, titleId } = useCoordinator();
  const appRoutes = useAppRoutes();

  const { data, isLoading, error, isError } = useGetItemsVersion(
    sectionThreeListId,
    itemNoVersionId,
  );
  const isVersionsForbidden = open && isError && isForbiddenError(error);

  useNotifyVersionsForbidden(error, isError, itemNoVersionId, open);

  const versions = isVersionsForbidden
    ? []
    : mapApiVersions(data?.section3List ?? []);

  const handleVersionRowClick = (version: JournalVersion) => {
    if (!itemNoVersionId) return;
    onCancel();
    const basePath = appRoutes.journals.section3View(
      projectId,
      objectId,
      mainJournalId,
      titleId,
      itemNoVersionId,
    );
    navigate(`${basePath}?versionNumber=${version.versionNumber}`);
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      className="journal-version-modal"
      footer={
        <div className="journal-version-modal-footer">
          <Button type="primary" onClick={onCancel}>
            Закрыть
          </Button>
        </div>
      }
      width={1100}
      title={null}
      destroyOnClose
    >
      <div className="journal-version-modal__title-wrap">
        <div className="journal-version-modal__crumb">
          Раздел №3 · Список версий
        </div>
        <div className="journal-version-modal__title">
          Запись №{sequenceNumber ?? "—"}
        </div>
      </div>

      <Spin spinning={isLoading && !isVersionsForbidden}>
        <JournalVersionsListPanel
          versions={versions}
          onVersionRowClick={handleVersionRowClick}
          versionDateColumnTitle="Дата и время создания версии"
          showChangeReasonColumn
          accessDenied={isVersionsForbidden}
          accessDeniedMessage={VERSIONS_ACCESS_DENIED_MESSAGE}
          tablePagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} из ${total} записей`,
          }}
        />
      </Spin>
    </Modal>
  );
};
